"use client";

import { useState } from "react";
import { Send } from "lucide-react";
import posthog from "posthog-js";

import { Button } from "@/components/ui/Button";

const inputClass =
  "w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-ink placeholder:text-mist/50 outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20";

// VOR-134: "Outro escopo / customizado" renomeado pra "Outro tipo".
const PROJECT_TYPES = [
  "AI-First Landing",
  "MVP SaaS com IA",
  "AI Site Institucional",
  "AI Audit + Fix",
  "Outro tipo",
] as const;

type ProjectType = (typeof PROJECT_TYPES)[number];

type Status =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "success" }
  | { kind: "error"; message: string };

/**
 * VOR-109 — formulário do Vorseum Studio.
 *
 * VOR-134 (2026-05-19): Bea revisou labels, placeholders e converteu o
 * seletor de tipo de projeto de radio buttons pra dropdown.
 *
 * Submit envia um POST pra /api/contact-studio que dispara um e-mail
 * via Resend pra studio@vorseum.com (alias criado em VINF-19).
 *
 * Honeypot (campo `website` invisível) pra reduzir spam.
 */
export function StudioContactForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [projectType, setProjectType] = useState<ProjectType | "">("");
  const [budget, setBudget] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  const submitting = status.kind === "submitting";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedMessage = message.trim();
    if (!trimmedName || !trimmedEmail || !projectType || !trimmedMessage) {
      return;
    }

    setStatus({ kind: "submitting" });

    try {
      const res = await fetch("/api/contact-studio", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: trimmedName,
          email: trimmedEmail,
          company: company.trim() || undefined,
          projectType,
          budget: budget.trim() || undefined,
          message: trimmedMessage,
          website,
        }),
      });

      if (!res.ok) {
        const payload = (await res.json().catch(() => ({}))) as {
          error?: string;
        };
        setStatus({
          kind: "error",
          message:
            payload.error ??
            "Não conseguimos enviar agora. Tenta de novo em instantes.",
        });
        return;
      }

      if (process.env.NEXT_PUBLIC_POSTHOG_TOKEN) {
        posthog.capture("studio_contact_submitted", {
          projectType,
          hasBudget: Boolean(budget.trim()),
        });
      }

      setStatus({ kind: "success" });
    } catch {
      setStatus({
        kind: "error",
        message: "Erro de rede. Verifica conexão e tenta novamente.",
      });
    }
  }

  if (status.kind === "success") {
    return (
      <div
        role="status"
        aria-live="polite"
        className="mt-10 rounded-3xl border border-success/30 bg-success/5 p-8 text-center"
      >
        <h2 className="font-display text-2xl font-medium text-white">
          Mensagem recebida!
        </h2>
        <p className="mt-3 text-sm leading-7 text-mist">
          Obrigada por entrar em contato. Respondemos em até 2 dias úteis
          pelo e-mail informado. Se for urgente, mande direto pra{" "}
          <a
            href="mailto:studio@vorseum.com"
            className="text-accent transition hover:underline"
          >
            studio@vorseum.com
          </a>
          .
        </p>
        <div className="mt-6">
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setName("");
              setEmail("");
              setCompany("");
              setProjectType("");
              setBudget("");
              setMessage("");
              setStatus({ kind: "idle" });
            }}
          >
            Enviar outra mensagem
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-10 space-y-5">
      <div>
        {/* VOR-134: label "Nome" → "Nome e sobrenome", placeholder removido */}
        <label
          htmlFor="studio-name"
          className="block text-sm font-medium text-white"
        >
          Nome e sobrenome
        </label>
        <input
          id="studio-name"
          type="text"
          required
          autoComplete="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={`${inputClass} mt-2`}
        />
      </div>

      <div>
        <label
          htmlFor="studio-email"
          className="block text-sm font-medium text-white"
        >
          E-mail
        </label>
        <input
          id="studio-email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="você@empresa.com"
          className={`${inputClass} mt-2`}
        />
      </div>

      <div>
        {/* VOR-134: "Empresa (opcional)" → "Nome da empresa" + remove "(opcional)" + placeholder */}
        <label
          htmlFor="studio-company"
          className="block text-sm font-medium text-white"
        >
          Nome da empresa
        </label>
        <input
          id="studio-company"
          type="text"
          autoComplete="organization"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          className={`${inputClass} mt-2`}
        />
      </div>

      <div>
        {/* VOR-134: converte radio pra dropdown */}
        <label
          htmlFor="studio-project-type"
          className="block text-sm font-medium text-white"
        >
          Tipo de projeto
        </label>
        <select
          id="studio-project-type"
          required
          value={projectType}
          onChange={(e) => setProjectType(e.target.value as ProjectType)}
          className={`${inputClass} mt-2`}
        >
          <option value="" disabled>
            Selecione uma opção
          </option>
          {PROJECT_TYPES.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </div>

      <div>
        {/* VOR-134: remove "(opcional)", placeholder vira support text */}
        <label
          htmlFor="studio-budget"
          className="block text-sm font-medium text-white"
        >
          Orçamento aproximado
        </label>
        <input
          id="studio-budget"
          type="text"
          value={budget}
          onChange={(e) => setBudget(e.target.value)}
          aria-describedby="studio-budget-help"
          className={`${inputClass} mt-2`}
        />
        <p id="studio-budget-help" className="mt-1.5 text-xs text-mist/70">
          R$ 30-50 mil, R$ 5 a 10 mil, em aberto etc.
        </p>
      </div>

      <div>
        {/* VOR-134: label "Conta sobre o projeto" → "Conte-nos sobre o projeto" + support text */}
        <label
          htmlFor="studio-message"
          className="block text-sm font-medium text-white"
        >
          Conte-nos sobre o projeto
        </label>
        <textarea
          id="studio-message"
          required
          rows={6}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          aria-describedby="studio-message-help"
          className={`${inputClass} mt-2 resize-y`}
        />
        <p id="studio-message-help" className="mt-1.5 text-xs text-mist/70">
          Objetivos, requisitos, referências, prazo estimado, complexidade, sistemas a considerar etc.
        </p>
      </div>

      {/* Honeypot — invisível pra humanos, atrai bots. */}
      <div aria-hidden="true" className="hidden">
        <label htmlFor="studio-website">Website (não preencha)</label>
        <input
          id="studio-website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>

      {status.kind === "error" ? (
        <p
          role="alert"
          className="rounded-2xl border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger"
        >
          {status.message}
        </p>
      ) : null}

      <div className="pt-2">
        <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
          {submitting ? "Enviando…" : (
            <>
              Enviar mensagem
              <Send size={16} aria-hidden="true" className="ml-2" />
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
